// Copy: plans/portal-api/usg-eol-portal-notices-copy.txt (Dave-approved 2026-09-24; do not reword).
import type React from 'react';
import { useId, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Notice, UsgService, StandaloneService, UispPhp2shellService } from '../../api/notices';
import { gatewaysCsvFilename, gatewaysCsvHref } from './gatewayCsv';
import { HELP_LINKS } from './helpLinks';
import { noticeSummary } from './noticeSummary';
import { uosUpgradeHref } from '../../utils/subscription';

const MAX_GATEWAYS = 5;

interface Action {
  label: string;
  href: string;
  internal?: boolean;
  // Blue button, and kept when the notice is collapsed. With none flagged, the first action is.
  primary?: boolean;
}

interface Props {
  notice: Notice;
  canDismiss: boolean;
  onDismiss: (id: string) => void;
  muted?: boolean;
  headingLevel?: 2 | 3;
  collapsible?: boolean;
  dismissDisabledReason?: string;
}

// Shared copy for the disabled close button shown while impersonating (canDismiss is false
// because of impersonation, not because the notice is already closed). AppLayout and NoticeBell
// both import this so the tooltip text can't drift between the two call sites.
export const IMPERSONATION_DISMISS_REASON =
  'Closing is disabled while impersonating. The customer can close this notice.';

function CloseIcon() {
  return (
    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function GatewayTable({ service }: { service: UsgService }) {
  const [showAll, setShowAll] = useState(false);
  const tableId = useId();
  const listed = service.gateways.length;
  const shown = showAll ? service.gateways : service.gateways.slice(0, MAX_GATEWAYS);
  // usg_count can exceed the list: the metrics collector stops at 100 devices.
  const more = Math.max(service.usg_count, listed) - shown.length;
  return (
    <div className="mt-3">
      <p className="text-sm font-medium text-gray-900">{service.hostname}</p>
      <div className="overflow-x-auto">
        <table id={tableId} className="mt-1 w-full text-sm text-left">
          <caption className="sr-only">{service.hostname}</caption>
          <thead className="text-gray-500">
            <tr><th className="pr-4 font-normal">Name</th><th className="pr-4 font-normal">Model</th><th className="pr-4 font-normal">Site</th><th className="font-normal">Status</th></tr>
          </thead>
          <tbody className="text-gray-800">
            {shown.map((g, i) => (
              <tr key={`${g.name}-${i}`}>
                <td className="pr-4">{g.name}</td>
                <td className="pr-4">{g.model}</td>
                <td className="pr-4">{g.site}</td>
                <td>{g.online ? 'Online' : 'Offline'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {more > 0 && <p className="mt-1 text-sm text-gray-600">and {more} more</p>}
      {listed > 0 && (
        <div className="mt-1 flex flex-wrap items-center gap-3">
          {listed > MAX_GATEWAYS && (
            <button
              type="button"
              aria-expanded={showAll}
              aria-controls={tableId}
              onClick={() => setShowAll((v) => !v)}
              className="text-sm font-medium text-blue-700 hover:underline"
            >
              {showAll ? 'Show fewer' : `Show all ${listed}`}
            </button>
          )}
          <a
            href={gatewaysCsvHref(service.gateways)}
            download={gatewaysCsvFilename(service.hostname)}
            className="text-sm font-medium text-blue-700 hover:underline"
          >
            Download list (CSV)
          </a>
        </div>
      )}
    </div>
  );
}

function usgContent(services: UsgService[]): { body: React.ReactNode; actions: Action[] } {
  const uos = services.some((s) => s.platform === 'uos');
  const intro = uos
    ? "Ubiquiti has announced that UniFi Network 10.6 is the last release that can manage the USG, USG-Pro-4 and USG-XG-8. Your console runs UniFi OS Server, which installs new Network releases automatically. Once it updates to Network 11, these gateways keep running with their current settings, but you won't be able to change their settings or adopt them again. Ubiquiti hasn't given a date for Network 11 yet."
    : "Ubiquiti has announced that UniFi Network 10.6 is the last release that can manage the USG, USG-Pro-4 and USG-XG-8. Your subscription runs the standalone Network Application, which stays on 10.6, so these gateways keep working as they do today. If you move to UniFi OS Server, they keep working there, but once Network 11 is released you won't be able to change their settings or adopt them again, so plan to replace them with a UXG gateway before then.";
  return {
    body: (
      <>
        <p>{intro}</p>
        {services.map((s) => <GatewayTable key={s.subscription_id} service={s} />)}
        {uos && (
          <p className="mt-3">
            We recommend planning to replace them with one of Ubiquiti's UXG gateways. If you need more time, you can keep
            your console on Network 10.6 by turning off Auto Update for the Network application in Settings &gt; Control
            Plane &gt; Updates.
          </p>
        )}
        {/* Added 2026-10-22 (PHP2Shell, Dave-approved): no version bump, so people who closed the banner see it in the bell. */}
        <p className="mt-3">
          The USG's own web page also runs an old PHP build that Ubiquiti won't update. It's only reachable from inside
          your network unless you've opened it to the internet, so keep it that way. The guide has the details.
        </p>
      </>
    ),
    actions: [
      { label: 'Read the guide', href: HELP_LINKS.usg },
      ...(uos ? [{ label: 'How to hold Network 10.6', href: HELP_LINKS.holdVersion }] : []),
    ],
  };
}

function standaloneContent(services: StandaloneService[], hasUsg: boolean): { body: React.ReactNode; actions: Action[] } {
  const selfServe = services.filter((s) => s.migration_path === 'self_serve');
  // Deep links that open the upgrade window: a bare /services/<id> link does nothing when the
  // customer is already on that page, and this notice shows on every page. One button per
  // service when there are several.
  const upgradeActions: Action[] =
    selfServe.length === 1
      ? [{ label: 'Upgrade to UniFi OS', href: uosUpgradeHref(selfServe[0].subscription_id), internal: true, primary: true }]
      : selfServe.map((s) => ({
          label: `Upgrade ${s.hostname}`,
          href: uosUpgradeHref(s.subscription_id),
          internal: true,
          primary: true,
        }));
  return {
    body: (
      <>
        <p>
          Ubiquiti has announced that 10.6 is the last release of the standalone UniFi Network Application, which your
          subscription runs. Network 11 hasn't been released yet and there's no release date, but when it arrives, it and
          all later releases will only be available on UniFi OS Server.
        </p>
        {selfServe.length > 0 ? (
          <>
            <p className="mt-3">To upgrade, move your subscription to UniFi OS Server with us from this portal:</p>
            <ol className="mt-1 list-decimal pl-5">
              <li>Open your service and click Upgrade to UniFi OS.</li>
              <li>Choose the console owner from your super admins.</li>
              <li>Review the details and start the migration.</li>
            </ol>
            <p className="mt-3">
              We build the new console, restore your data into it and keep your existing address working, so your devices
              reconnect on their own. Most upgrades take 20 to 30 minutes.
            </p>
          </>
        ) : (
          <p className="mt-3">
            To move to UniFi OS Server, open your service and click Request Migration Access. We'll let you know when
            migration is enabled for your account, and you can then start the move from this portal. If you don't see
            the button, contact our support team.
          </p>
        )}
        {hasUsg && (
          <p className="mt-3">
            Your USG gateways keep working after the move. Once Network 11 is released for UniFi OS Server, their
            settings can no longer be changed. See the USG notice for how to plan their replacement.
          </p>
        )}
      </>
    ),
    actions: [
      ...upgradeActions,
      { label: 'Before you move', href: HELP_LINKS.migrating },
    ],
  };
}

function stripeContent(): { body: React.ReactNode; actions: Action[] } {
  return {
    body: (
      <p>
        We're moving all Cloud UniFi billing to Stripe, and your account is still on our old billing system. Switching
        takes about a minute. Your service, hostname and settings don't change, and any time you've already paid for
        carries over, so you won't be charged again until it runs out.
      </p>
    ),
    actions: [
      { label: 'Switch to Stripe', href: '/billing', internal: true },
      { label: 'Questions about switching', href: HELP_LINKS.stripe },
    ],
  };
}

// Copy: plans/portal-api/uisp-php2shell-portal-notice-copy.txt (design approved by Dave 2026-10-22).
function php2shellServiceText(s: UispPhp2shellService): string {
  return s.crm === 'blocked'
    ? `Until Ubiquiti releases a fixed UISP version, we have blocked public access to the CRM module on ${s.hostname}. Your CRM holds no client records, so nothing you use changes; the CRM tab shows an access denied page for now.`
    : `Until Ubiquiti releases a fixed UISP version, the CRM module on ${s.hostname} stays available but file uploads to it are blocked, including CSV imports and attachments, which fail with a 415 error. Billing, the client zone and online payments keep working.`;
}

function uispPhp2shellContent(services: UispPhp2shellService[]): { body: React.ReactNode; actions: Action[] } {
  const ending = services.every((s) => s.crm === 'blocked') ? 'block' : 'restriction';
  return {
    body: (
      <>
        <p>
          Two critical vulnerabilities in PHP, known as PHP2Shell, affect the CRM module that Ubiquiti ships with UISP.
          UISP itself does not use PHP, and nothing else in your Cloud UniFi service does.
        </p>
        {services.map((s) => (
          <p key={s.subscription_id} className="mt-3">{php2shellServiceText(s)}</p>
        ))}
        <p className="mt-3">
          The rest of UISP, including your devices and monitoring, is unaffected, and we found no sign of the
          vulnerability being used against your service. We will apply Ubiquiti's fix within 24 hours of its release
          and remove the {ending}.
        </p>
      </>
    ),
    actions: [{ label: 'Read the details', href: HELP_LINKS.uispPhp2shell }],
  };
}

// Exhaustiveness guard: TS narrows `notice` to `never` here once every NoticeKey case above is
// handled, so a future notice key that isn't handled yet is a compile error, not a crash. At
// runtime (e.g. an unknown key that reaches this component directly, bypassing getNotices's
// filter), render nothing rather than pass a malformed value on to the JSX below.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function assertNever(_notice: never): null {
  return null;
}

function noticeContent(notice: Notice): { body: React.ReactNode; actions: Action[] } | null {
  switch (notice.key) {
    case 'uisp-php2shell':
      return uispPhp2shellContent(notice.data.services);
    case 'usg-eol':
      return usgContent(notice.data.services);
    case 'standalone-eol':
      return standaloneContent(notice.data.services, notice.data.has_usg);
    case 'stripe-billing':
      return stripeContent();
    default:
      return assertNever(notice);
  }
}

function ActionLink({ action, primary }: { action: Action; primary: boolean }) {
  const className = primary
    ? 'rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700'
    : 'text-sm font-medium text-blue-700 hover:underline';
  return action.internal ? (
    <Link to={action.href} className={className}>
      {action.label}
    </Link>
  ) : (
    <a href={action.href} target="_blank" rel="noopener noreferrer" className={className}>
      {action.label}
    </a>
  );
}

export default function NoticeBanner({
  notice,
  canDismiss,
  onDismiss,
  muted = false,
  headingLevel = 2,
  collapsible = false,
  dismissDisabledReason,
}: Props) {
  const [expanded, setExpanded] = useState(false);
  const detailsId = useId();
  const content = noticeContent(notice);
  if (!content) return null;
  const { body, actions } = content;
  const HeadingTag = headingLevel === 3 ? 'h3' : 'h2';
  const isCollapsed = collapsible && !expanded;
  const flaggedPrimary = actions.filter((a) => a.primary);
  const primaryActions = flaggedPrimary.length > 0 ? flaggedPrimary : actions.slice(0, 1);

  return (
    <section
      aria-label={notice.title}
      className={
        isCollapsed
          ? `border-l-4 px-4 py-3 ${muted ? 'border-gray-300 bg-gray-50 opacity-80' : 'border-blue-500 bg-blue-50'}`
          : `rounded-lg border p-4 ${muted ? 'border-gray-200 bg-gray-50 opacity-80' : 'border-blue-200 bg-blue-50'}`
      }
    >
      <div className="flex items-start justify-between gap-4">
        <HeadingTag className="text-base font-semibold text-gray-900">{notice.title}</HeadingTag>
        {canDismiss ? (
          <button
            type="button"
            aria-label="Close notice"
            onClick={() => onDismiss(notice.id)}
            className="rounded p-1 text-gray-500 hover:bg-gray-200 hover:text-gray-800"
          >
            <CloseIcon />
          </button>
        ) : (
          dismissDisabledReason && (
            <button
              type="button"
              aria-label="Close notice (disabled)"
              aria-disabled="true"
              disabled
              title={dismissDisabledReason}
              className="rounded p-1 text-gray-300 cursor-not-allowed"
            >
              <CloseIcon />
            </button>
          )
        )}
      </div>

      {isCollapsed && <p className="mt-2 text-sm text-gray-700">{noticeSummary(notice)}</p>}

      {!isCollapsed && (
        <div id={collapsible ? detailsId : undefined} className="mt-2 text-sm text-gray-700">
          {body}
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-3">
        {(isCollapsed ? primaryActions : actions).map((a) => (
          <ActionLink key={a.label} action={a} primary={primaryActions.includes(a)} />
        ))}
        {collapsible && (
          <button
            type="button"
            aria-expanded={expanded}
            aria-controls={expanded ? detailsId : undefined}
            onClick={() => setExpanded((v) => !v)}
            className="text-sm font-medium text-blue-700 hover:underline"
          >
            {expanded ? 'Hide details' : 'Show details'}
          </button>
        )}
      </div>
    </section>
  );
}
